import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import * as d3 from 'd3'

import { pluralize } from '@aytm/app/stats/lib/util/helpers'
import { getVectorIcon, getVisibleRaw } from '@aytm/app/stats/page/data-centrifuge/lib/vectors'
import {
  DEFAULT_QUARK,
  DONE_QUARK,
  FULFILL_QUARK,
  formatPoint,
  formatTranslate,
  calcMiddlePoint,
  calcCirclePosition
} from '@aytm/app/stats/page/data-centrifuge/lib/utils'
import {
  getD,
  getKeys,
  getVectorColor,
  getVectorDarkerColor,
  getVectorStrokeColor,
  getVectorStrokeWidth,
  getTextColor,
  getIconColor,
  getCircleColor,
  getLinkColor,
  getLinkOpacity,
  getLinkCurve,
  getScoreArc,
  getScoreArcData,
  getScoreGradient
} from '@aytm/app/stats/page/data-centrifuge/lib/d3-helpers'

import { DEFAULT_CONFIG, useDataCalculation } from '@aytm/app/stats/page/data-centrifuge/hooks/useDataCalculation'

import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'
import { useLoader } from '@aytm/app/stats/page/data-centrifuge/components/Loader'
import { useTooltip } from '@aytm/app/stats/page/data-centrifuge/components/Tooltip'

// styles
import style from '@aytm/app/stats/page/data-centrifuge/styles/Chart.module.scss'

// Self-measured fallback width for the first paint; the ResizeObserver in useComponentWidth
// corrects it to the real container width on mount.
const DEFAULT_WIDTH = 600

// Per-vector "running" spinner. A bare circle rotation is invisible (a circle is
// rotationally symmetric), so we dash the stroke into a ring and spin THAT — the
// classic loading look. Two things make this safe alongside the other transitions:
//   • a NAMED transition ('spin') so the default-named transitions animateSVG starts
//     on the same circle (radius) run concurrently and never cancel the rotation;
//   • an `el.__spinning` flag + `isConnected` guard so the self-re-arming .on('end')
//     loop stops the instant the vector stops running or leaves the DOM — no detached
//     node keeps a timer alive.
// ms per full revolution; the ~21 dashes on an r=40 ring make the perceived speed ~21x this.
const SPIN_DURATION = 6000
const startSpin = node => {
  const el = node.node()
  if (!el || el.__spinning) return
  el.__spinning = true

  const circle = node.selectAll('.group-vector-circle')
  circle.attr('stroke-dasharray', '6 6')

  const repeat = () => {
    if (!el.isConnected || !el.__spinning) return
    circle
      .attr('transform', 'rotate(0)')
      .transition('spin')
      .ease(d3.easeLinear)
      .duration(SPIN_DURATION)
      .attrTween('transform', () => _t => `rotate(${360 * _t})`)
      .on('end', repeat)
  }
  repeat()
}
const stopSpin = node => {
  const el = node.node()
  if (!el || !el.__spinning) return
  el.__spinning = false

  node.selectAll('.group-vector-circle').interrupt('spin').attr('transform', 'rotate(0)').attr('stroke-dasharray', null)
}

const Chart = ({
  tier,
  reviewEvidence,
  // Data calculation props
  job,
  vectors,
  disabledVectors,
  autoEnabledLikeVectors = [],
  filter = 'all',
  suspicionLevel = 0,
  scores = [],
  suspicionScores = {},
  quarantineUIDs = [],
  dataVersion,
  // Additional props
  scoresFiltered,
  // Configuration props
  vectorRadius = DEFAULT_CONFIG.vectorRadius,
  vectorGravity = DEFAULT_CONFIG.vectorGravity,
  minScoreRadius = DEFAULT_CONFIG.minScoreRadius,
  maxScoreRadius = DEFAULT_CONFIG.maxScoreRadius,
  curveAngle = DEFAULT_CONFIG.curveAngle,
  maxD = DEFAULT_CONFIG.maxD,
  minD = DEFAULT_CONFIG.minD,
  power = DEFAULT_CONFIG.power,
  padding = DEFAULT_CONFIG.padding,
  stroke = DEFAULT_CONFIG.stroke,
  linkOpacity = DEFAULT_CONFIG.linkOpacity,
  jitter = DEFAULT_CONFIG.jitter,
  dataCacheEnabled = DEFAULT_CONFIG.dataCacheEnabled,
  onCallback
}) => {
  // Self-measure the container instead of receiving width through the prop chain — the
  // embedded chart fits whatever surface hosts it (e.g. the DQR accordion section).
  const { width, componentRef } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH })

  // `job` is rebuilt upstream on every parent render (the loader's
  // per-tween-tick onCallback keeps re-emitting the Backbone state back into
  // React). Layout only depends on the job *phase* (status + per-vector
  // running/done transitions), NOT on progress percentages — so we memoise a
  // reference that is stable across progress-only updates. The loader below
  // still receives the raw `job` because it *does* need each progress
  // tick. Fixes over-firing of the calcData effect (BUG-D).
  const layoutJob = useMemo(
    () => job,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [job?.status, job?.vectors]
  )

  // Use data calculation hook
  const { data } = useDataCalculation({
    width,
    job: layoutJob,
    vectors,
    disabledVectors,
    filter,
    suspicionLevel,
    scores,
    suspicionScores,
    quarantineUIDs,
    dataVersion,
    vectorRadius,
    vectorGravity,
    minScoreRadius,
    maxScoreRadius,
    curveAngle,
    maxD,
    minD,
    power,
    padding,
    jitter,
    dataCacheEnabled
  })

  const [selectedVector, setSelectedVector] = useState(null)

  const svgRef = useRef(null)
  const containerRef = useRef(null)

  const animationQuarkRef = useRef(DEFAULT_QUARK)
  const animationQuarkDelayRef = useRef(0)

  // Live `job` mirror for the Loader's progress tween. The loader
  // needs to read the current job.progress.* on every animation frame, but we
  // do NOT want the loader to re-register its transitions every onCallback
  // tick — that was BUG-D: renderLoader.interrupt() → restart loop thrashed
  // the spin + progress-fill tweens. We pass `layoutJob` (stable
  // across progress ticks) for phase-change triggering, and `jobRef`
  // for live progress reads inside the tween.
  const jobRef = useRef(job)
  const selectedVectorRef = useRef(null)

  const chartWidth = width - 2
  const chartHeight = width - 2

  // Initialize loader hook. `job` here is the phase-only memo — the
  // loader re-registers its transitions only on status / per-vector
  // running/done transitions. Live progress numbers are read through
  // `jobRef` inside the tween (see Loader.jsx).
  const { renderLoader } = useLoader({
    svgRef,
    jobRef,
    data,
    job: layoutJob,
    chartHeight,
    onCallback
  })

  // Initialize tooltip hook
  const { handleScoreClick, renderTooltip, cleanupTooltip } = useTooltip({
    svgRef,
    tier,
    reviewEvidence,
    data,
    linkOpacity,
    selectedVector
  })

  // Select vector handler
  const handleSelectVector = useCallback(d => setSelectedVector(selectedVectorRef.current != d.id ? d.id : null), [])

  // Update vector scores selection
  const selectVectorScores = useCallback(() => {
    if (!svgRef.current) return

    svgRef.current.selectAll('.group-vector').each(function (d) {
      const node = d3.select(this)

      node.selectAll('.group-vector-circle').attr('stroke', getVectorStrokeColor(d, selectedVectorRef.current))

      node.selectAll('.group-vector-text text').attr('fill', getTextColor(d, selectedVectorRef.current))
      node.selectAll('.group-vector-icon path').attr('fill', getIconColor(d, selectedVectorRef.current))
    })

    svgRef.current.selectAll('.group-score').each(function (d) {
      const node = d3.select(this)

      node.selectAll('.group-score-circle').attr('fill', getCircleColor(d, selectedVectorRef.current))
    })

    svgRef.current.selectAll('.group-score .group-score-arc').each(function (d) {
      d3.select(this)
        .attr('d', getScoreArc(getScoreArcData(d, data, selectedVectorRef.current), stroke))
        .attr('fill', function () {
          return getScoreGradient(d, data, d3.select(this?.parentNode), selectedVectorRef.current)
        })
    })

    svgRef.current.selectAll('.group-link').each(function (d) {
      d3.select(this)
        .attr('stroke', getLinkColor(d, selectedVectorRef.current))
        .style('opacity', getLinkOpacity(d, data, linkOpacity, selectedVectorRef.current))
    })
  }, [data, stroke, linkOpacity])

  // Render Links
  const renderLinks = useCallback(() => {
    if (!svgRef.current || !data) return

    const svg = svgRef.current

    let g = svg.selectAll('.group-links').data([data.links])
    g.exit().remove()
    g.enter().append('g').attr('class', 'group-links').attr('transform', `translate(${data.center.x},${data.center.y})`)

    g = svg.selectAll('.group-links')

    // Links are keyed by their stable id (vector-<uid>-<vectorId>), so a link that only
    // moves is reused (update) and its datum re-bound — never removed. enter draws it
    // collapsed at the vector (start); animateSVG transitions both enter and update to
    // `end`. The trailing stroke/opacity apply to enter+update, so reused links also pick
    // up colour/opacity changes (selection, recompute) that animateSVG doesn't touch.
    g.selectAll('.group-link')
      .data(getD, getKeys)
      .join(
        enter =>
          enter
            .append('path')
            .attr('id', d => `group-link-${d.id}`)
            .attr('class', 'group-link')
            .attr('data-testid', 'stat-dc-link')
            .attr('data-uid', d => d.score.id)
            .attr('data-vector', d => d.vector.id)
            .attr('transform', 'translate(0,0)')
            .attr('d', d => getLinkCurve(d.start.map(p => formatPoint(p, data.angle))))
            .attr('fill', 'none')
            .attr('stroke', d => getLinkColor(d, selectedVectorRef.current))
            .attr('stroke-width', 1)
            .style('opacity', 0),
        update =>
          update.each(function (d) {
            d3.select(this).attr('stroke', getLinkColor(d, selectedVectorRef.current))
          }),
        exit => exit.attr('class', 'group-link-removed').remove()
      )

    g.selectAll('.group-link').each(function (_d, i) {
      d3.select(this).attr('index', i)
    })
  }, [data, linkOpacity])

  // Render Scores
  const renderScores = useCallback(() => {
    if (!svgRef.current || !data) return

    const svg = svgRef.current

    let g = svg.selectAll('.group-scores').data([data.scores])
    g.exit().remove()
    g.enter()
      .append('g')
      .attr('class', 'group-scores')
      .attr('transform', `translate(${data.center.x},${data.center.y})`)

    g = svg.selectAll('.group-scores')

    // Scores are keyed by id only (not position), so a moved score is reused (update) and
    // repositioned by animateSVG instead of being exit+entered.
    g.selectAll('.group-score')
      .data(getD, getKeys)
      .join(
        enter => {
          const enterG = enter
            .append('g')
            .attr('id', d => `group-score-${d.id}`)
            .attr('class', 'group-score')
            .attr('data-testid', 'stat-dc-score-node')
            .attr('data-uid', d => d.id)
            .attr('transform', d => formatTranslate(d, data.angle))
            .style('opacity', 0)
            .on('mousedown', function (_event, d) {
              handleScoreClick(this, d)
            })

          enterG
            .append('circle')
            .attr('class', 'group-score-circle')
            .attr('cx', 0)
            .attr('cy', 0)
            .attr('r', d => d.r)
            .attr('fill', d => getCircleColor(d, selectedVectorRef.current))
            .style('cursor', 'pointer')

          return enterG
        },
        update =>
          update.each(function (d) {
            const node = d3.select(this)
            node
              .selectAll('.group-score-circle')
              .attr('r', d.r)
              .attr('fill', getCircleColor(d, selectedVectorRef.current))
          }),
        exit => exit.attr('class', 'group-score-removed').remove()
      )

    g.selectAll('.group-score').each(function (_d, i) {
      d3.select(this).attr('index', i)
    })

    // Arc groups (one wrapper per score)
    g.selectAll('.group-score')
      .selectAll('.group-score-arcs')
      .data(d => [d.arcs])
      .join(enter =>
        enter
          .append('g')
          .attr('class', 'group-score-arcs')
          .attr('transform', 'translate(0,0)')
          .style('cursor', 'pointer')
      )

    // Arc paths — keyed by id; reused arcs get d/fill refreshed on enter+update so a data
    // change without a selection still repaints them (previously they were recreated when
    // the parent score churned on position).
    g.selectAll('.group-score-arcs')
      .selectAll('.group-score-arc')
      .data(getD, getKeys)
      .join(
        enter =>
          enter
            .append('path')
            .attr('class', 'group-score-arc')
            .attr('transform', 'translate(0,0)')
            .attr('d', d => getScoreArc(getScoreArcData(d, data, selectedVectorRef.current), stroke))
            .attr('fill', function (d) {
              return getScoreGradient(d, data, d3.select(this?.parentNode), selectedVectorRef.current)
            }),
        update =>
          update.each(function (d) {
            d3.select(this)
              .attr('d', getScoreArc(getScoreArcData(d, data, selectedVectorRef.current), stroke))
              .attr('fill', function () {
                return getScoreGradient(d, data, d3.select(this?.parentNode), selectedVectorRef.current)
              })
          }),
        exit => exit.attr('class', 'group-score-arc-removed').remove()
      )
  }, [data, stroke, handleScoreClick])

  // Render Vectors
  const renderVectors = useCallback(() => {
    if (!svgRef.current || !data) return

    const svg = svgRef.current
    const k = 0.65
    const t = 0.75

    let g = svg.selectAll('.group-vectors').data([data.vectors])
    g.exit().remove()
    g.enter()
      .append('g')
      .attr('class', 'group-vectors')
      .attr('transform', `translate(${data.center.x},${data.center.y})`)

    g = svg.selectAll('.group-vectors')

    // Vectors are keyed by id only (getKeys), so each vector is created once and thereafter
    // reused — both position moves and status changes flow through the `update` branch, which
    // re-applies the status-dependent visuals (gradient / stroke width / link opacity); there
    // is no enter-rebuild on status change. animateSVG handles repositioning.
    g.selectAll('.group-vector')
      .data(getD, getKeys)
      .join(
        enter => {
          const gsEnter = enter
            .append('g')
            .attr('id', d => `group-vector-${d.id}`)
            .attr('class', 'group-vector')
            .attr('data-testid', 'stat-dc-vector')
            .attr('data-vector', d => d.id)
            .attr('transform', d => formatTranslate(d, data.angle))

          gsEnter
            .append('defs')
            .append('linearGradient')
            .attr('id', d => `group-vector-${d.id}-gradient`)
            .append('stop')
            .attr('class', 'group-vector-gradient-start')
            .attr('stop-color', d => getVectorColor(d))
            .attr('stop-opacity', 1)
            .attr('offset', '0')
            .select(function () {
              return this.parentNode
            })
            .append('stop')
            .attr('class', 'group-vector-gradient-end')
            .attr('stop-color', d => getVectorDarkerColor(d))
            .attr('stop-opacity', 1)
            .attr('offset', '1')

          // Link
          gsEnter
            .append('path')
            .attr('class', 'group-vector-link')
            .attr('transform', 'translate(0,0)')
            .attr('d', d => {
              const p = formatPoint(d, data.angle)
              const m = calcMiddlePoint(
                [
                  { x: -p.x, y: -p.y },
                  { x: 0, y: 0 }
                ],
                30
              )

              return getLinkCurve([{ x: -p.x, y: -p.y }, m, { x: 0, y: 0 }])
            })
            .attr('fill', 'none')
            .attr('stroke', d => d.color)
            .attr('stroke-width', 1)
            .style('opacity', d => (['running', 'done'].includes(d.status) ? 1 : 0))

          // Circle
          gsEnter
            .append('circle')
            .attr('class', 'group-vector-circle')
            .attr('cx', 0)
            .attr('cy', 0)
            .attr('r', 0)
            .attr('fill', '#ffffff')
            .attr('stroke', d => getVectorStrokeColor(d, selectedVectorRef.current))
            .attr('stroke-width', d => getVectorStrokeWidth(d))
            .style('cursor', 'pointer')

          // Text
          gsEnter
            .append('g')
            .attr('class', 'group-vector-text')
            .attr('transform', 'translate(0,0)')
            .style('opacity', 0)
            .style('cursor', 'pointer')
            .append('path')
            .attr('id', d => `group-vector-text-path-${d.id}`)
            .attr(
              'd',
              d =>
                `M 0,${t * d.r - 1} A ${t * d.r - 1},${t * d.r - 1} 0 0, 1 0,-${t * d.r - 1} A ${t * d.r - 1},${
                  t * d.r - 1
                } 0 0, 1 0,${t * d.r - 1}`
            )
            .attr('fill', 'none')
            .attr('stroke', '#ffffff00')
            .attr('stroke-width', 0)
            .select(function () {
              return this.parentNode
            })
            .append('text')
            .attr('fill', d => getTextColor(d, selectedVectorRef.current))
            .style('font-size', d => `${(1 - t) * d.r}px`)
            .style('line-height', d => `${(1 - t) * d.r}px`)
            .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
            .append('textPath')
            .text(d => d.text)
            .attr('href', d => `#group-vector-text-path-${d.id}`)
            .attr('startOffset', '50%')
            .style('text-anchor', 'middle')

          // Icon
          gsEnter
            .append('g')
            .attr('class', 'group-vector-icon')
            .attr('transform', d => `translate(-${k * d.r},-${k * d.r - 4}) scale(${(k * d.r) / 50})`)
            .style('opacity', 0)
            .style('cursor', 'pointer')
            .append('path')
            .attr('d', d => getVectorIcon(d.id))
            .attr('fill', d => getIconColor(d, selectedVectorRef.current))

          // Click handler
          gsEnter.on('click', (_event, d) => {
            if (d.status != undefined) return
            handleSelectVector(d)
          })

          // Spin vectors that appear already running (started mid-job).
          gsEnter.each(function (d) {
            if (d.status == 'running') startSpin(d3.select(this))
          })

          return gsEnter
        },
        update =>
          update.each(function (d) {
            const node = d3.select(this)

            node.selectAll('.group-vector-gradient-start').attr('stop-color', getVectorColor(d))
            node.selectAll('.group-vector-gradient-end').attr('stop-color', getVectorDarkerColor(d))

            node.selectAll('.group-vector-link').style('opacity', ['running', 'done'].includes(d.status) ? 1 : 0)

            node
              .selectAll('.group-vector-circle')
              .attr('stroke', getVectorStrokeColor(d, selectedVectorRef.current))
              .attr('stroke-width', getVectorStrokeWidth(d))

            node.selectAll('.group-vector-text text').attr('fill', getTextColor(d, selectedVectorRef.current))
            node.selectAll('.group-vector-icon path').attr('fill', getIconColor(d, selectedVectorRef.current))

            // Status changes are keyed-by-id updates (not enter rebuilds), so the
            // spinner is started/stopped here as the vector enters/leaves 'running'.
            if (d.status == 'running') startSpin(node)
            else stopSpin(node)
          }),
        exit => {
          exit
            .attr('class', 'group-vector-removed')
            .each(function (d) {
              const node = d3.select(this)

              stopSpin(node)

              node.selectAll('defs').remove()

              node.selectAll('.group-vector-link').remove()

              if (!d.status) {
                node.selectAll('.group-vector-text').remove()
                node.selectAll('.group-vector-icon').remove()
              }
            })
            .transition()
            .duration(animationQuarkRef.current / 2)
            .selectAll('.group-vector-circle')
            .attr('r', 2)
            .select(function () {
              return this.parentNode
            })
            .remove()
        }
      )
  }, [data, handleSelectVector])

  // Render Result Text
  const renderText = useCallback(() => {
    if (!svgRef.current) return

    const svg = svgRef.current
    // getVisibleRaw() excludes admin-hidden vectors so the displayed total isn't inflated
    // by vectors that aren't shown in the UI at all. The auto-enabled-like addend cancels
    // the disabled-vectors subtraction for ran-clean vectors the suspicion-exclude seed
    // placed in disabledVectors, so a vector the engine ran successfully always counts.
    const vectorsLength =
      vectors.length +
      autoEnabledLikeVectors.length -
      getVisibleRaw().filter(item => disabledVectors.includes(item)).length

    const text = `${scoresFiltered.length} ${pluralize(
      scoresFiltered.length,
      'response',
      'responses'
    )} from ${vectorsLength} ${pluralize(vectorsLength, 'vector', 'vectors')}`

    let result = svg.selectAll('.group-result')
    if (result.empty()) {
      result = svg
        .append('g')
        .attr('class', 'group-result')
        .attr('transform', `translate(${chartWidth - 10},${chartHeight - 10})`)

      result
        .append('text')
        .attr('class', 'group-result-text')
        .attr('data-testid', 'stat-dc-summary')
        .attr('fill', '#444444')
        .text('')
        .style('font-size', '14px')
        .style('line-height', '16px')
        .style('font-family', 'Ubuntu, Calibri, Segoe, Helvetica, Arial')
        .style('font-style', 'italic')
        .style('text-anchor', 'end')
    }

    result.selectAll('.group-result-text').text(text)
  }, [vectors, disabledVectors, autoEnabledLikeVectors, scoresFiltered, chartWidth, chartHeight])

  // Animate SVG
  const animateSVG = useCallback(() => {
    if (!svgRef.current || !data) return

    const svg = svgRef.current
    const r = data.R + vectorRadius + maxScoreRadius

    svg.selectAll('.group-vector').each(function (d) {
      d3.select(this)
        .transition()
        .delay(animationQuarkDelayRef.current)
        .duration(animationQuarkRef.current)
        .attrTween('transform', function (d) {
          const node = d3.select(this)

          const transform = node.attr('transform')
          const match = transform.match(/translate\(([^,]+),([^)]+)\)/)
          const translate = match ? [parseFloat(match[1]), parseFloat(match[2])] : [0, 0]

          const p1 = { x: translate[0], y: translate[1] }
          const p2 = formatPoint(d, data.angle)

          return _t => {
            const x = (1 - _t) * p1.x + _t * p2.x
            const y = (1 - _t) * p1.y + _t * p2.y

            if ((p1.x == 0 && p1.y == 0) || (p2.x == 0 && p2.y == 0)) {
              return `translate(${x},${y})`
            }

            const p = calcCirclePosition(x, y, r)
            return `translate(${p.x},${p.y})`
          }
        })

      const node = d3.select(this)

      node
        .selectAll('.group-vector-link')
        .transition()
        .delay(animationQuarkDelayRef.current)
        .duration(animationQuarkRef.current)
        .attr('d', () => {
          const p = formatPoint(d, data.angle)
          const m = calcMiddlePoint(
            [
              { x: -p.x, y: -p.y },
              { x: 0, y: 0 }
            ],
            30
          )

          return getLinkCurve([{ x: -p.x, y: -p.y }, m, { x: 0, y: 0 }])
        })

      node
        .selectAll('.group-vector-circle')
        .transition()
        .delay(animationQuarkDelayRef.current)
        .duration(animationQuarkRef.current)
        .attr('r', d.r)

      node
        .selectAll('.group-vector-text')
        .transition()
        .delay((3 * animationQuarkRef.current) / 4)
        .duration(animationQuarkRef.current / 4)
        .style('opacity', 1)
      node
        .selectAll('.group-vector-icon')
        .transition()
        .delay((3 * animationQuarkRef.current) / 4)
        .duration(animationQuarkRef.current / 4)
        .style('opacity', 1)
    })

    svg.selectAll('.group-score').each(function (d) {
      d3.select(this)
        .transition()
        .delay(
          job?.status == 'completed'
            ? animationQuarkDelayRef.current + animationQuarkRef.current / 2
            : animationQuarkDelayRef.current
        )
        .duration(job?.status == 'completed' ? animationQuarkRef.current / 2 : animationQuarkRef.current)
        .attr('transform', formatTranslate(d, data.angle))
        .style('opacity', 1)
    })

    svg.selectAll('.group-link').each(function (d) {
      d3.select(this)
        .transition()
        .delay(
          job?.status == 'completed'
            ? animationQuarkDelayRef.current + animationQuarkRef.current / 2
            : animationQuarkDelayRef.current
        )
        .duration(job?.status == 'completed' ? animationQuarkRef.current / 2 : animationQuarkRef.current)
        .attr('d', getLinkCurve(d.end.map(p => formatPoint(p, data.angle))))
        .style('opacity', getLinkOpacity(d, data, linkOpacity, selectedVectorRef.current))
    })

    if (null != selectedVectorRef.current) selectVectorScores()
  }, [data, vectorRadius, maxScoreRadius, selectVectorScores])

  useEffect(() => {
    jobRef.current = job
  }, [job])

  // Sync the ref in an effect so render stays pure (required under React 18 concurrent /
  // strict-mode — writing to a ref during render can commit a value from a render that is
  // later thrown away). A layout refresh that drops the selected vector clears the
  // selection, so the ref lands on the cleared value before the render effect below
  // (declared after this one) paints with it.
  useEffect(() => {
    const dropped = null != selectedVector && null != data && !data.vectors.some(v => selectedVector == v.id)

    if (dropped) setSelectedVector(null)
    selectedVectorRef.current = dropped ? null : selectedVector
  }, [selectedVector, data])

  // Repaint on a selection CHANGE only. A data change must not repaint from here: it would
  // read the new layout against a DOM the D3 joins have not reconciled yet, so an arc whose
  // score has just left the layout resolves no score row. The post-refresh repaint belongs
  // to animateSVG, which runs after those joins.
  useEffect(
    () => selectVectorScores(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedVector]
  )

  // DC-v3: slow the per-element transitions during the 'fulfill' phase so the
  // visual swap from "spinning loader" to "fully rendered chart" reads as a
  // single graceful transition instead of N independent ones. After fulfill
  // (status == 'done' or anything later) we restore the normal-speed quark.
  useEffect(() => {
    animationQuarkRef.current =
      job?.status == 'fulfill'
        ? FULFILL_QUARK
        : job?.status == 'done'
          ? DONE_QUARK
          : job?.status == 'completed'
            ? DEFAULT_QUARK / 2
            : DEFAULT_QUARK
    animationQuarkDelayRef.current = job?.status == 'completed' ? DEFAULT_QUARK / 2 : 0
  }, [job?.status])

  // Create the SVG once on mount and own its teardown on unmount: interrupt every
  // D3 transition the render effects started — the default-named ones (animateSVG, the
  // loader spin loop) plus the named 'spin' vector spinner (plain .interrupt() only
  // targets the default name) — then drop the node. Split from the sizing effect below
  // so this teardown fires ONLY on unmount, not on every resize (which previously
  // interrupted in-flight animations mid-tween).
  useEffect(() => {
    if (!containerRef.current) return

    svgRef.current = d3.select(containerRef.current).append('svg').attr('data-testid', 'stat-dc-chart')

    return () => {
      if (svgRef.current) {
        svgRef.current.selectAll('*').interrupt().interrupt('spin')
        svgRef.current.remove()
        svgRef.current = null
      }
    }
  }, [])

  // Dismiss the tooltip on unmount — removes its document mousedown listener and clears
  // its pending hide timer. cleanupTooltip is referentially stable (useCallback []).
  useEffect(() => cleanupTooltip, [cleanupTooltip])

  // Track container resizes — (re)apply width / height to the existing svg.
  useEffect(() => {
    if (!svgRef.current) return
    svgRef.current.attr('width', chartWidth).attr('height', chartHeight)
  }, [chartWidth, chartHeight])

  // Main render effect. `renderLoader` now carries phase changes via its
  // own memoization over `layoutJob`; `job` (the per-tick
  // progress-bearing object) is intentionally NOT a dep here — progress
  // updates flow through `jobRef` inside the loader's attrTween.
  useEffect(() => {
    if (!svgRef.current || !data) return

    renderLinks()
    renderScores()
    renderVectors()
    renderText()
    renderLoader()

    animateSVG()
  }, [data, renderLinks, renderScores, renderVectors, renderText, renderLoader, animateSVG])

  return (
    <div className={style.component} ref={componentRef}>
      <div ref={containerRef} />
      {renderTooltip()}
    </div>
  )
}

Chart.propTypes = {
  tier: PropTypes.string,
  reviewEvidence: PropTypes.bool,
  // Data calculation props
  job: PropTypes.shape({
    status: PropTypes.string,
    vectors: PropTypes.object,
    progress: PropTypes.shape({
      current: PropTypes.number,
      real: PropTypes.number,
      vector: PropTypes.shape({
        current: PropTypes.number,
        real: PropTypes.number
      })
    })
  }),
  vectors: PropTypes.array,
  disabledVectors: PropTypes.array,
  autoEnabledLikeVectors: PropTypes.array,
  filter: PropTypes.oneOf(['all', 'flagged', 'quarantined']),
  suspicionLevel: PropTypes.number,
  scores: PropTypes.array,
  suspicionScores: PropTypes.object,
  quarantineUIDs: PropTypes.array,
  dataVersion: PropTypes.number,
  // Additional props
  scoresFiltered: PropTypes.array,
  // Configuration props
  vectorRadius: PropTypes.number,
  vectorGravity: PropTypes.number,
  minScoreRadius: PropTypes.number,
  maxScoreRadius: PropTypes.number,
  curveAngle: PropTypes.number,
  maxD: PropTypes.number,
  minD: PropTypes.number,
  power: PropTypes.number,
  padding: PropTypes.number,
  stroke: PropTypes.number,
  linkOpacity: PropTypes.shape({
    single: PropTypes.shape({
      min: PropTypes.number,
      power: PropTypes.number
    }),
    multi: PropTypes.shape({
      min: PropTypes.number,
      power: PropTypes.number,
      coef: PropTypes.number
    })
  }),
  jitter: PropTypes.bool,
  dataCacheEnabled: PropTypes.bool,
  onCallback: PropTypes.func
}

Chart.displayName = 'Chart'

export default Chart
